import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {UiQuestionnaireLayout, Col, UiText, Row, UiButton, UiFormControl, UiTextarea} from '~/components/elements';
import {
  BIRTH_CONTROL_TELECONSULTATION_KEYS,
  MENOPAUSE_TELECONSULTATION_KEYS,
  METHOD_KEYS,
  YES_OPTION,
} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer, setNextQuestionnaire, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire10Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {loading} = useRequestState(addQuestionnaire);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === MENOPAUSE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
  );
  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);
  const currentSymptomAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[MENOPAUSE_TELECONSULTATION_KEYS.SELECT_SYMPTOMS]?.data,
  );
  const {wpAuthUser} = medicalQuestionnaire;
  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[MENOPAUSE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]?.data,
  );

  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    medicationsTaking: currentAnswer?.medicationsTaking || '',
  });
  const onClickSubmit = async () => {
    let currentSymptomData = currentSymptomAnswer;
    if (currentSymptomAnswer?.selectedSymptomOther) {
      const {selectedSymptomOther, ...rest} = currentSymptomAnswer;
      currentSymptomData = rest;
    }
    const questionnaireAnswer = {
      key: MENOPAUSE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: inputs,
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [MENOPAUSE_TELECONSULTATION_KEYS.SELECT_SYMPTOMS]: {
              ...medicalQuestionnaireAnswer[MENOPAUSE_TELECONSULTATION_KEYS.SELECT_SYMPTOMS],
              data: currentSymptomData,
            },
            [MENOPAUSE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.MENOPAUSE_TELECONSULTATION,
        },
      }),
    );
    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(
          `/appointment?type=${type}&methodKey=${METHOD_KEYS.MENOPAUSE_TELECONSULTATION}&token=${savedToken}`,
        );
      }
    }
  };

  const renderRelations = () => {
    const relations = questionnaire?.options?.find(relation => relation?.value === inputs?.selectedOption)?.relations;
    return (
      <>
        {relations?.map((relation, index) => (
          <Col key={index} w="100%">
            <UiText fontWeight={700} fontSize="16px" mt={spacing.small}>
              {relation?.questions?.default}
            </UiText>
            <UiFormControl id="medicationsTaking" isRequired>
              <UiTextarea
                mt={spacing.small}
                bg="white"
                borderRadius={spacing.tiny}
                placeholder="Provide as much detail as possible"
                defaultValue={inputs?.medicationsTaking}
                onChange={({target}) => setInputs({medicationsTaking: target.value})}
                minH="200px"
              />
            </UiFormControl>
          </Col>
        ))}
      </>
    );
  };
  const isDisable = !inputs?.selectedOption || (inputs?.selectedOption === YES_OPTION && !inputs?.medicationsTaking);

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisable}
      loading={loading}
    >
      <Col align="center" textAlign="center" h="100%">
        <UiText variant="mdqTitle" my={10}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row justifyContent="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({selectedOption: value?.value})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              mb={6}
              variant={inputs?.selectedOption === value.value ? 'solid' : 'toggle'}
              w="100%"
              mx={1}
            />
          ))}
        </Row>
        {inputs?.selectedOption !== YES_OPTION &&
          questionnaire?.descriptions?.default?.map(desc => (
            <UiText key={desc} color="liteOuter" fontSize="14px">
              {desc}
            </UiText>
          ))}
        {renderRelations()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire10Screen;
